'use client';
import './round-results.css';

import { Guess, PuzzleResultsState } from '@wt/app/game/_components/types';
import { GameMapDistanceElement } from '@wt/app/game/_elements';
import { usePuzzleTodays } from '@wt/app/game/_hooks/usePuzzleTodays';
import { getGameScore } from '@wt/app/game/_utils/getGameScore';
import ImageModal from '@wt/game/modals/ImageModal';
import MapDistanceModal from '@wt/game/modals/MapDistanceModal';
import { useRoundProgress } from '@wt/game/providers/roundProgress/RoundProgressProvider';
import useSettingsContext from '@wt/game/providers/settings/types';
import { useUserProvider } from '@wt/game/providers/user/UserProvider';
import { getDayString } from '@wt/game/utils/getDayString';
import { SidebarAd } from '@wt/shared/components/ads/SidebarAd';
import { PuzzleLocation } from '@wt/shared/components/maps/mapkit/types';
import { refreshSidebarAd } from '@wt/shared/hooks/useAds/useAds';
import useReactive from '@wt/shared/hooks/useReactive';
import { isEmpty } from 'lodash';
import Link from 'next/link';
import { redirect } from 'next/navigation';
import { memo, useEffect } from 'react';
import JSConfetti from 'js-confetti';

import { PUZZLE_LENGTH } from '../PuzzleLoader';
import { RoundResultsDesktop } from './RoundResultsDesktop';
import { RoundResultsMobile } from './RoundResultsMobile';
import HintWalletModal from '@wt/game/modals/HintWallet';
import { useResponsive } from '@wt/shared/hooks/useResponsive';
import { getSiteMode } from '@wt/utilities/siteMode';
import { getScoreIcon } from '../RoundSummary';

export const MediaLisence = memo(function MediaLisence({
  info_url,
  photographer,
  lisence,
  userSubmitted,
}: {
  info_url: string;
  photographer: string | undefined;
  lisence: string;
  userSubmitted: boolean;
}) {
  const str = photographer ? `Photo credit: ${photographer}` : ``;
  return isEmpty(info_url) ? (
    <div className="z-10 line-clamp-2 text-sm italic md:mb-4 md:text-base">
      {str}
    </div>
  ) : (
    <Link
      className="z-10 line-clamp-2 text-sm italic md:mb-4 md:text-base lg:hover:underline"
      href={info_url}
      target="_blank"
      rel="noopener noreferrer"
    >
      {str}
    </Link>
  );
});

export const RoundResultsFooterNav = memo(
  function RoundResultsFooterNav(props: { lastRound: undefined | boolean }) {
    const { gameMode, puzzleId } = useRoundProgress();
    const suffix =
      gameMode != 'daily' && puzzleId
        ? `/${gameMode}/game/${puzzleId}`
        : '/game';
    return (
      <>
        {!props.lastRound ? (
          <Link
            href={suffix}
            prefetch={true}
            className="btn btn-primary w-full shrink text-lg font-bold uppercase text-primary-content md:h-[61px] lg:text-2xl"
          >
            NEXT ROUND
          </Link>
        ) : (
          <Link
            href={suffix + '/summary'}
            prefetch={true}
            className="btn btn-primary w-full shrink text-lg font-bold uppercase text-primary-content md:h-[61px] lg:text-2xl"
          >
            VIEW RESULTS
          </Link>
        )}
      </>
    );
  }
);

export const MapDistanceMemo = memo(function MapDistanceElem(props: {
  guess: Guess;
  puzzle: PuzzleLocation;
  showHintButton?: boolean;
}) {
  return (
    <div className="relative size-full">
      <GameMapDistanceElement
        id={'map-distance-modal'}
        guess={props.guess}
        showHintButton={props.showHintButton}
        to={{
          latitude: props.puzzle.latitude,
          longitude: props.puzzle.longitude,
        }}
      />
    </div>
  );
});

const RoundResultsContainer = ({
  results,
}: {
  results: PuzzleResultsState;
}) => {
  const [settings] = useSettingsContext();
  const responsiveInfo = useResponsive();

  const lastGuess = results.currentRound
    ? results.guesses && results.guesses[results.currentRound - 1]
    : results.guesses && results.guesses[results.guesses.length - 1];
  return (
    <div
      className="flex size-full flex-1 flex-col justify-between"
      data-round={results.round}
    >
      <div className="grow">
        {!responsiveInfo.lg && (
          <div className="flex size-full">
            <RoundResultsMobile
              results={results}
              settings={settings}
              lastGuess={lastGuess}
              roundNumber={results.currentRound ? results.currentRound : 0}
            />
          </div>
        )}
        {responsiveInfo.lg && (
          <div className="flex lg:h-full ipad:flex-col-reverse desktop:flex-row">
            <SidebarAd />
            <RoundResultsDesktop
              results={results}
              settings={settings}
              lastGuess={lastGuess}
            />
          </div>
        )}
      </div>
      {lastGuess && (
        <>
          <MapDistanceModal id={'map-distance-modal'} guess={lastGuess} />
          <ImageModal
            src={lastGuess.puzzle.url}
            alt={''}
            id="puzzle-image-modal"
          />
          <HintWalletModal mode="results" />
        </>
      )}
    </div>
  );
};

const alreadyAnimatedElements = new Set<string>();
export const initialiseRoundResultAnimations = (node: HTMLDivElement) => {
  if (node) {
    const key =
      (node.getAttribute('data-puzzle-id') ?? '') +
      ':' +
      (node.getAttribute('data-current-round') ?? '');
    const alreadyAnimated = alreadyAnimatedElements.has(key);
    const disableAnimationSetting =
      node.getAttribute('data-should-animate') === 'false';
    alreadyAnimatedElements.add(key);

    const shouldAnimate = !alreadyAnimated && !disableAnimationSetting;

    // Find all elements with "data-animate-element" attribute, and give them the class "animate"
    const elements = node.querySelectorAll('[data-animate-element]');
    const animateOrder = shouldAnimate
      ? getSiteMode() != 'wheretaken'
        ? [
            { tag: 'scroll-to-bottom', delay: 400 },
            { tag: 'year-icon', delay: 800 },
            { tag: 'year-text', delay: 800 },
            { tag: 'year-guess', delay: 800 },
            { tag: 'location-icon', delay: 1400 },
            { tag: 'location-text', delay: 1400 },
            { tag: 'location-guess', delay: 1400 },
            { tag: 'description', delay: 2000 },
            { tag: 'year-score-pill', delay: 2800 },
            { tag: 'location-score-pill', delay: 3400 },
            { tag: 'total-score-pill', delay: 4000 },
            { tag: 'bot-info', delay: 4600 },
          ]
        : [
            { tag: 'scroll-to-bottom', delay: 400 },
            { tag: 'location-icon', delay: 800 },
            { tag: 'location-text', delay: 800 },
            { tag: 'location-guess', delay: 800 },
            { tag: 'description', delay: 1400 },
            { tag: 'total-score-pill', delay: 2600 },
          ]
      : [];
    let animateDelta = 0;
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];
      const animateTag = element.getAttribute('data-animate-element');
      const animateInfo = animateOrder.find((item) => item.tag === animateTag);
      if (!animateInfo) {
        element.classList.add('!opacity-100');
      } else {
        setTimeout(() => {
          if (node.getRootNode() !== document) {
            console.log('Navigated away from the page, skipping animation');
            return;
          }
          element.classList.add('!opacity-100', '!translate-y-0');
          if (animateTag === 'total-score-pill') {
            const totalScore = +(element.getAttribute('data-score') ?? 0);
            if (totalScore >= 75) {
              let confettiNumber = 150;
              if (totalScore >= 175) {
                confettiNumber = 350;
              } else if (totalScore >= 125) {
                confettiNumber = 250;
              }
              // If there is confetti, subsequent animations are delayed
              animateDelta += 600;
              setTimeout(() => {
                if (node.getRootNode() !== document) {
                  console.log(
                    'Navigated away from the page, skipping animation'
                  );
                  return;
                }
                const canvas = document.getElementById('confetti-canvas');
                if (canvas) {
                  const jsConfetti = new JSConfetti({
                    canvas: canvas as HTMLCanvasElement,
                  });
                  const scoreIcon = getScoreIcon(totalScore);
                  jsConfetti.addConfetti({
                    confettiNumber,
                  });
                  jsConfetti.addConfetti({
                    emojis: [scoreIcon],
                    emojiSize: window.devicePixelRatio > 1 ? 100 : 50,
                    confettiNumber: 20,
                  });
                }
              }, 600);
            }
          } else if (animateTag == 'scroll-to-bottom') {
            // Scroll to the bottom of the element
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
            });
          }
        }, animateInfo.delay + animateDelta);
      }
    }
  }
};

const RoundResults = ({ round }: { round: string }) => {
  const { gameMode, puzzleId, puzzle } = useRoundProgress();
  const state: PuzzleResultsState = useReactive({
    date: puzzleId ?? getDayString(0),
    guesses: undefined,
    device: '',
    score: 0,
    currentRound: Number(round),
    round: -1,
    puzzle: puzzle,
  });

  const [todays] = usePuzzleTodays(
    puzzleId ?? state.date,
    puzzle.length,
    gameMode
  );
  const [settings] = useSettingsContext();
  const {
    setScore,
    setNumRoundsCompleted,
    setCurrentRound,
    setViewingResults,
  } = useRoundProgress();
  const { updateStats } = useUserProvider();

  useEffect(() => {
    if (todays && todays.guesses && settings) {
      if (todays.guesses && todays.guesses.length === 0) {
        return gameMode != 'daily' && puzzleId
          ? redirect(`/${gameMode}/game/${puzzleId}`)
          : redirect('/game');
      }
      state.guesses = todays.guesses;
      state.score =
        todays.guesses &&
        todays.guesses.reduce((c, pg) => {
          const ps = getGameScore(pg, settings.distanceUnit);
          return c + ps.score.total;
        }, 0);
      setScore && setScore(state.score);
      state.currentRound = round !== '' ? Number(round) : todays.guesses.length;
      setCurrentRound && setCurrentRound(state.currentRound);
      state.round = todays.guesses.length;
      setNumRoundsCompleted && setNumRoundsCompleted(todays.guesses.length);
      if (state.currentRound > state.round) {
        return gameMode != 'daily' && puzzleId
          ? redirect(`/${gameMode}/game/${puzzleId}`)
          : redirect('/game');
      }

      // save daily stats if game has been completed
      if (gameMode == 'daily' && todays.guesses.length === PUZZLE_LENGTH) {
        updateStats(todays.guesses, state.date);
      }
    }
  }, [todays, settings]);

  useEffect(() => {
    refreshSidebarAd();
    setViewingResults && setViewingResults(true);
  }, []);

  return (
    <div className="relative flex size-full flex-col items-center justify-center">
      <RoundResultsContainer results={state} />
    </div>
  );
};

export default memo(RoundResults);
